/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import React, { useState, useRef, useEffect, useImperativeHandle } from "react";
import { Card, Form, Button, Row, Col } from "react-bootstrap";
import { toast } from "react-hot-toast";
import * as S from "./styles";

const is = require("is_js");

export interface IAppCardCompProps {
  presentationList: any[];
  parentDownloadRef: any;
  onFetchLabels?(data: any): void;
  onAddSerialNo?(data: any): void;
}

export const AppCardComp = ({
  presentationList,
  parentDownloadRef,
  onFetchLabels,
  onAddSerialNo,
}: IAppCardCompProps) => {
  const presentationRef = useRef<HTMLSelectElement>(null);
  const sectionRef = useRef<HTMLSelectElement>(null);
  const sectionOptionTypeRef = useRef<HTMLSelectElement>(null);
  const childButtonRef = useRef(null);

  const [selectedPresentation, setSelectedPresentation] = useState<any>(null);
  const [selectedSection, setSelectedSection] = useState<any>(null);

  useEffect(() => {
    if (selectedPresentation) {
      setSelectedSection(null);
      sectionRef.current!.value = "";
      sectionOptionTypeRef.current!.value = "";
    }
  }, [selectedPresentation]);

  useEffect(() => {
    if (selectedSection) {
      sectionOptionTypeRef.current!.value = "";
    }
  }, [selectedSection]);

  const handleClick = () => {
    if (childButtonRef.current) {
      childButtonRef.current.click();
    }
  };

  useImperativeHandle(parentDownloadRef, () => ({
    handleClick,
  }));

  const _onFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const selectedPresentationId = presentationRef.current!.value;
    const selectedPresentation = presentationList.find(
      (presentation: any) => presentation.id === selectedPresentationId
    );

    const selectedSectionId = sectionRef.current!.value;
    const selectedSection = selectedPresentation?.sections.find(
      (section: any) => section.id === selectedSectionId
    );

    const selectedSectionOptionTypeId = sectionOptionTypeRef.current!.value;
    const selectedSectionOptionType = selectedSection?.costumeTypes.find(
      (costumeType: any) => costumeType.id === selectedSectionOptionTypeId
    );

    const selectedValues = {
      presentation: selectedPresentation,
      section: selectedSection,
      sectionOptionType: selectedSectionOptionType,
    };

    const submitterButton = event?.nativeEvent?.submitter;

    if (selectedValues?.presentation?.id === "") {
      toast.error("Please select a Presentation");
    } else if (selectedValues?.section?.id === "") {
      toast.error("Please select a Section");
    } else if (selectedValues?.sectionOptionType?.id === "") {
      toast.error("Please select a Section Option Type");
    } else {
      if (submitterButton && submitterButton?.name === "addSerialNo") {
        onAddSerialNo && onAddSerialNo(selectedValues);
      } else {
        onFetchLabels && onFetchLabels(selectedValues);
      }
    }
  };

  const handlePresentationChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedPresentationId = event.target.value;
    const selectedPresentation = presentationList.find(
      (presentation: any) => presentation.id === selectedPresentationId
    );
    setSelectedPresentation(selectedPresentation);
  };

  const handleSectionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedSectionId = event.target.value;
    const selectedSection = selectedPresentation?.sections.find(
      (section: any) => section.id === selectedSectionId
    );
    setSelectedSection(selectedSection);
  };

  const _renderHeader = () => {
    return <S.HeaderTitle>Print Labels</S.HeaderTitle>;
  };

  const _renderPresentationField = () => {
    return (
      <Col sm={6} md={4}>
        <Form.Group controlId="presentation">
          <Form.Label style={{ color: "white" }}>Presentation</Form.Label>
          <Form.Select
            aria-label="Default select example"
            ref={presentationRef}
            placeholder="Select"
            onChange={handlePresentationChange}
            style={S.SelectButtonStyle}
          >
            {presentationList?.map((presentation: any) => (
              <option key={presentation.id} value={presentation.id}>
                {presentation.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>
    );
  };

  const _renderSectionField = () => {
    return (
      <Col sm={6} md={4}>
        <Form.Group controlId="section">
          <Form.Label style={{ color: "white" }}>Section</Form.Label>
          <Form.Select
            aria-label="Default select example"
            ref={sectionRef}
            placeholder="Select"
            onChange={handleSectionChange}
            style={S.SelectButtonStyle}
          >
            {selectedPresentation?.sections?.map((section: any) => (
              <option key={section.id} value={section.id}>
                {section.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>
    );
  };

  const _renderSectionOptionField = () => {
    return (
      <Col sm={6} md={4}>
        <Form.Group controlId="sectionOptionType">
          <Form.Label style={{ color: "white" }}>
            Section Option Type
          </Form.Label>
          <Form.Select
            aria-label="Default select example"
            ref={sectionOptionTypeRef}
            placeholder="Select"
            style={S.SelectButtonStyle}
          >
            {selectedSection?.costumeTypes?.map((costumeType: any) => (
              <option key={costumeType.id} value={costumeType.id}>
                {costumeType.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>
    );
  };

  const _renderFetchLabelsButton = () => {
    return (
      <Col sm={12} md={12} className="d-flex align-items-end">
        <Button
          variant="secondary"
          style={{ marginRight: "15px", padding: "8px 15px" }}
          className="animate-up-2"
          type="submit"
          name="addSerialNo"
        >
          Add Serial No & Download
        </Button>
        <Button
          variant="secondary"
          style={{ padding: "8px 30px" }}
          className="animate-up-2"
          type="submit"
          name="fetchLabels"
          ref={childButtonRef}
        >
          Download Labels
        </Button>
      </Col>
    );
  };

  return (
    <div style={{ margin: "30px 0", minHeight: "70vh" }}>
      {_renderHeader()}
      <S.CardContainer>
        <Card.Body>
          <Form onSubmit={_onFormSubmit}>
            <Row className="mb-4">
              {_renderPresentationField()}
              {_renderSectionField()}
              {_renderSectionOptionField()}
            </Row>
            <Row className="mb-4">{_renderFetchLabelsButton()}</Row>
          </Form>
        </Card.Body>
      </S.CardContainer>
    </div>
  );
};

AppCardComp.defaultProps = {
  presentationList: [],
};

export default AppCardComp;
